// ============================================================
// TERROR — THE DRAW PULSE
// ------------------------------------------------------------
// caP'n, July 28: "Only on Turn one, for both players, have the
// Luxmoire radiate with a yellow Lyt(light ;3) that pulses so it
// draws players attention so they don't forget to draw a card on
// their Luxmoire since they can't draw from the Grim. Then after
// turn one, the light stays on as normal for the Lux, however,
// now there is a Red version for the Grimoire >:3"
// Then: "add variations of the pulse to the booster shop :3 quick
// different colors and eventually we'll get more technical and
// make Legendary and even Mythical."
//
// ── WHO PULSES, AND WHEN (caP'n's ruling, verbatim) ─────────
// "only player who goes first, the player who goes second (even
//  on the second turn) gets both pulses"
//
//   TURN 0 — the first player's first turn. R07 seals their
//            GRIMOIRE. Only the LUX pulses, and the Grim tile now
//            reads SEALED instead of pretending to be clickable.
//   TURN 1 — the second player's first turn. Their Grimoire is
//            NOT sealed (Rulez_v2: the seal is first-player only),
//            so BOTH pulse — Lux gold, Grim red.
//   TURN 2+ — no BEAT. Both tiles sit lit as normal; the Grim now
//            carries its own red identity instead of borrowing
//            purple. The pulse is a first-turn teacher, not decor.
//
// ── ⭐ TONE vs BEAT (caP'n, 2026-08-18) ─────────────────────
// *"They should retain that cool aura around them, not just for the
//  draw on turn one — I hope it stays there persistent throughout
//  the Solrend. Doesn't have to pulse, but the cool unique aura
//  colour you bought should remain."*
//
// He is right, and the first cut conflated two different things
// under one word:
//
//   TONE — WHAT COLOUR the pile wears. Cosmetic. Bought. Belongs to
//          the player for the whole duel, every turn, always.
//   BEAT — WHETHER it breathes. Instructional. A first-turn teacher
//          that dies the moment it stops being true.
//
// They were one flag, so turning the teacher off also stripped the
// paint and the tiles fell back to default gold/red from turn 2 —
// which meant a 600◕ cosmetic was visible for exactly one turn.
//
// Now: `restStyle()` is the tone with no animation, applied for the
// whole Solrend; `toneStyle()` is the same colours WITH the beat,
// applied only while the cue is live. Same custom properties, same
// zero-keyframe cost, one extra CSS rule.
//
// The cue also dies the instant it stops being true: it requires
// `myTurn && canDraw && !drawnThisTurn`, so it never pulses at a
// player who has already drawn, and never on the adversary's seat.
//
// ── WHY THIS IS NOT A 7th COSMETIC SLOT ─────────────────────
// IE Cosmetic Schema v1.1 locks SIX slots (head/face/body/back/
// aura/title) and says in writing: "do NOT add new top-level
// slots." A Luxmoire does not exist in Snake Eyes or Pierre, so
// an equip-anywhere/appear-everywhere slot would be meaningless
// there — this is a TERROR-LOCAL BOARD COSMETIC, and Terror
// already has that lane: `roomBackdrops`. The Signature shelf in
// the Emporium is documented as "backdrops today; any future cool
// stuff (auras, flourishes, music, room props) gets shelved here
// too." This is that. Same shelf, same economy, same append-only
// unlock, its own node — zero schema break, nothing to propagate
// to the other two apps.
//
// ── THE REWARD CLASSES ──────────────────────────────────────
// FABLE / LEGENDARY / MYTHIC, per `ie-cross-game-economy`. Every
// pulse here is FABLE — caP'n's "regular Fable versions." The
// tier ladder is already wired, so a Legendary or Mythic pulse is
// a data row, not an engine change.
//
// ⚠ THE MYTHIC LAW, banked now so it is not forgotten later:
// "Every mythic item needs a unique signature effect — never a
// recolor of a lower tier." Fable pulses ARE recolors, and that is
// correct for Fable. A Mythic pulse must do something no colour
// can — read `fx` below: it is the seam that exists for exactly
// that, and today every entry leaves it null.
//
// ── THE ONE RULE A VARIATION MAY NOT BREAK ──────────────────
// Lux and Grim colours must stay clearly DISTINCT from each
// other inside a single pulse. The two-colour split is not
// decoration, it is how a player tells the two piles apart at a
// glance mid-duel. Recolour freely; never make them twins.
//
// ── PERF ────────────────────────────────────────────────────
// ONE keyframe, driven by CSS custom properties. Colours are set
// inline per tile, so N variations cost N zero extra keyframes and
// the animation stays on box-shadow/opacity only. No setState, no
// rAF, nothing per-frame — `ie-realtime-perf` untouched.
// ============================================================

import { useState, useEffect } from "react";
import { ref, onValue } from "firebase/database";
import { db, auth } from "./firebase";

// ── REWARD CLASSES — mirrors BACKDROP_RARITY exactly ─────────
export const PULSE_RARITY = {
  fable:     { label: "FABLE",     color: "#b3a68c" },
  legendary: { label: "LEGENDARY", color: "#e8b84b" },
  mythic:    { label: "MYTHIC",    color: "#f43f5e" },
  // Legacy aliases → folded into the three (back-compat only)
  common:    { label: "FABLE",     color: "#b3a68c" },
  uncommon:  { label: "FABLE",     color: "#b3a68c" },
  rare:      { label: "LEGENDARY", color: "#e8b84b" },
  epic:      { label: "LEGENDARY", color: "#e8b84b" },
};

export const DEFAULT_PULSE_ID = "lyt";

// ── THE CATALOG ──────────────────────────────────────────────
// `base` is the resting edge, `hot` is the top of the beat.
// `ms`   is one full breath. `fx` is the Legendary/Mythic seam —
//        a signature behaviour beyond colour. Null on every Fable
//        entry by design (see THE MYTHIC LAW above).
export const DRAW_PULSES = [
  {
    id: "lyt", name: "Lyt", rarity: "fable", cost: 0, fx: null,
    lore: "The light every Aenarchist is issued. Gold for the Lux, blood for the Grim. It has never needed to be anything else.",
    lux:  { base: "#e8b84b", hot: "#fff3c4" },
    grim: { base: "#dc2626", hot: "#ffb0b0" },
    ms: 1150,
  },
  {
    id: "khao_bloom", name: "Khao Bloom", rarity: "fable", cost: 1, fx: null,
    lore: "Raw Khao, leaking from the rift beneath the pile. It breathes slower than you do.",
    lux:  { base: "#a855f7", hot: "#f0ddff" },
    grim: { base: "#ff2f8a", hot: "#ffd0e6" },
    ms: 1450,
  },
  {
    id: "deepglass", name: "Deepglass", rarity: "fable", cost: 1, fx: null,
    lore: "Cold light off something submerged. The Gatekeepers' colour, borrowed without asking.",
    lux:  { base: "#22d3ee", hot: "#dcfcff" },
    grim: { base: "#3b82f6", hot: "#dbeafe" },
    ms: 1250,
  },
  {
    id: "emberfall", name: "Emberfall", rarity: "fable", cost: 1, fx: null,
    lore: "R'uk's leavings. The Lux smoulders, the Grim catches — and neither of them ever quite goes out.",
    lux:  { base: "#f59e0b", hot: "#ffe6b0" },
    grim: { base: "#b91c1c", hot: "#ff9f7a" },
    ms: 980,
  },
  {
    id: "jynxis_grin", name: "J'ynxi's Grin", rarity: "fable", cost: 1, fx: null,
    lore: "Trickster's emerald, on your side of the board for once. Friend or faux — it pulses either way.",
    lux:  { base: "#2ee66b", hot: "#d8ffe6" },
    grim: { base: "#d4a84b", hot: "#fff3c4" },
    ms: 1080,
  },
];

export const PULSE_BY_ID = Object.fromEntries(DRAW_PULSES.map(p => [p.id, p]));

// Resolve an id to a pulse. NEVER throws, NEVER returns null —
// an unknown or retired id falls back to Lyt, so a cosmetic that
// gets pulled from the shelf can't leave a board with no cue.
export const getDrawPulse = (id) => PULSE_BY_ID[id] || PULSE_BY_ID[DEFAULT_PULSE_ID];

// ── THE RECIPE ───────────────────────────────────────────────
// One keyframe for every variation, forever. Colours ride in as
// CSS custom properties, so adding a pulse never adds a keyframe.
const DP_KEYFRAMES = `
@keyframes dpBeat {
  0%,100% { box-shadow: 0 0 9px var(--dp-b), inset 0 0 6px var(--dp-b), 0 2px 6px rgba(0,0,0,0.5); }
  50%     { box-shadow: 0 0 28px var(--dp-h), 0 0 52px var(--dp-b),
                        inset 0 0 15px var(--dp-h), 0 2px 6px rgba(0,0,0,0.5); }
}
@keyframes dpRing  { 0% { opacity:.5; transform:scale(1) } 100% { opacity:0; transform:scale(1.7) } }
@keyframes dpGlyph { 0%,100% { opacity:.72; transform:scale(1) } 50% { opacity:1; transform:scale(1.14) } }
`;
export function ensureDrawPulseKeyframes() {
  if (typeof document === "undefined") return;
  if (document.getElementById("dp-keyframes")) return;
  const el = document.createElement("style");
  el.id = "dp-keyframes"; el.textContent = DP_KEYFRAMES;
  document.head.appendChild(el);
}
ensureDrawPulseKeyframes();

// Style fragment for a pulsing tile. `which` is "lux" | "grim".
// Spread it onto the tile's style object — it brings its own
// custom properties AND its own animation, so a call site never
// has to know which colours it is wearing.
export function pulseStyle(pulse, which) {
  const p = pulse && pulse[which] ? pulse : getDrawPulse(DEFAULT_PULSE_ID);
  return toneStyle(p[which] || p.lux, p.ms);
}

// The same recipe one level down, for callers that already hold a
// TONE rather than a whole pulse — the board hands its draw tiles
// a resolved { base, hot, ms } and should not have to carry the
// catalog entry along with it just to re-look-up its own colour.
export function toneStyle(tone, ms = 1150) {
  const t = tone && tone.base ? tone : getDrawPulse(DEFAULT_PULSE_ID).lux;
  return {
    "--dp-b": t.base,
    "--dp-h": t.hot,
    animation: `dpBeat ${t.ms || ms || 1150}ms ease-in-out infinite`,
  };
}

// ── THE RESTING AURA — tone without beat ─────────────────────
// What a pile wears for the rest of the Solrend once the teacher has
// done its job. Same custom properties as the beat so a call site can
// swap between them without knowing which colours it holds, and
// deliberately NO `animation` key: a resting aura that breathes is
// just a quieter pulse, and caP'n asked for a colour, not a slower
// heartbeat.
//
// The glow is a touch under the beat's resting frame (9px → 8px, and
// the inset drops) so that when the beat DOES start on turn one it
// still reads as an escalation rather than as the same thing louder.
export function restStyle(tone) {
  const t = tone && tone.base ? tone : getDrawPulse(DEFAULT_PULSE_ID).lux;
  return {
    "--dp-b": t.base,
    "--dp-h": t.hot,
    boxShadow: `0 0 8px ${t.base}, inset 0 0 5px ${t.base}55, 0 2px 6px rgba(0,0,0,0.5)`,
  };
}

// Just the colours, for callers that light something themselves
// (the 3D piles take a plain colour string, not a CSS animation).
export const pulseTone = (pulse, which) => {
  const p = pulse && pulse[which] ? pulse : getDrawPulse(DEFAULT_PULSE_ID);
  return p[which] || p.lux;
};

// ── THE EQUIP HOOK ───────────────────────────────────────────
// uid is OPTIONAL. Shatterfield is handed `code / playerIdx /
// collection` and no uid, and threading one down through the
// whole duel screen to colour a border would be a lot of plumbing
// for a cosmetic — so this falls back to the signed-in auth uid,
// which is the same person by definition. Passing a uid still
// wins when a caller has one (the shop does).
export function useEquippedDrawPulse(uid = null) {
  const [pulse, setPulse] = useState(() => getDrawPulse(DEFAULT_PULSE_ID));

  useEffect(() => {
    const id = uid || auth.currentUser?.uid || null;
    if (!id) { setPulse(getDrawPulse(DEFAULT_PULSE_ID)); return; }
    const off = onValue(ref(db, `profiles/${id}/drawPulse/equipped`), (s) => {
      setPulse(getDrawPulse(typeof s.val() === "string" ? s.val() : DEFAULT_PULSE_ID));
    });
    return () => off();
  }, [uid]);

  return pulse;
}

// ── LIVE PREVIEW — what the shop shelf shows ─────────────────
// Two real tiles, pulsing exactly as they will on the board. No
// screenshots, no mockups: a player buys precisely what they see.
export function DrawPulsePreview({ pulse, size = 46, gap = 14, showLabels = true }) {
  const tile = (which, glyph, label) => {
    const tone = pulseTone(pulse, which);
    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 5 }}>
        <div style={{
          position: "relative", width: size, height: size * 1.16, borderRadius: 5,
          background: "linear-gradient(145deg,#241c0c,#1a1509)",
          border: `1.5px solid ${tone.base}`,
          display: "flex", alignItems: "center", justifyContent: "center",
          ...pulseStyle(pulse, which),
        }}>
          <span style={{
            fontSize: Math.round(size * 0.42), lineHeight: 1,
            filter: `drop-shadow(0 0 5px ${tone.hot})`,
            animation: "dpGlyph 1.6s ease-in-out infinite",
          }}>{glyph}</span>
        </div>
        {showLabels && (
          <span style={{
            fontSize: 7.5, letterSpacing: 2, fontWeight: 900,
            color: tone.base, fontFamily: "'Cinzel', serif",
          }}>{label}</span>
        )}
      </div>
    );
  };
  return (
    <div style={{ display: "flex", gap, alignItems: "center", justifyContent: "center" }}>
      {tile("lux", "\u2726", "LUX")}
      {tile("grim", "\uD83D\uDCD6", "GRIM")}
    </div>
  );
}
